import type { RotinaGrupo } from '@/services/api/rotinas';
import { hhmm, horaExibida } from './rotinaUtils';

const emMinutos = (hora: string) => {
    const [h, m] = hhmm(hora).split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
};

const duracao = (min: number) => {
    const h = Math.floor(min / 60);
    const m = min % 60;
    if (h === 0) return `${m}min`;
    return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, '0')}`;
};

const comoHora = (min: number) =>
    `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;

/**
 * Os disparos da Rotina ao longo das 24h, no horário de Brasília — a mesma
 * régua da tabela e do calendário.
 *
 * A hora de cada disparo vem ESCRITA, alternando acima e abaixo do trilho: a
 * versão anterior era uma fileira de tracinhos iguais, que mostrava que existem
 * dez disparos sem dizer que horas são nenhum deles — para saber, era preciso
 * ler a tabela, e aí o gráfico não servia para nada.
 *
 * O maior intervalo sem disparo aparece destacado porque é a única leitura que
 * a tabela não dá de graça: ela está ordenada por hora, mas o buraco entre duas
 * linhas só salta aqui.
 */
export default function LinhaDoTempo({ grupos }: { grupos: RotinaGrupo[] }) {
    const marcas = grupos
        .map((g) => ({ g, min: emMinutos(horaExibida(g)) }))
        .sort((a, b) => a.min - b.min);

    const ativos = marcas.filter((m) => m.g.ativa);
    let maior: { gap: number; de: number; ate: number } | null = null;
    for (let i = 1; i < ativos.length; i++) {
        const gap = ativos[i].min - ativos[i - 1].min;
        if (!maior || gap > maior.gap) {
            maior = { gap, de: ativos[i - 1].min, ate: ativos[i].min };
        }
    }

    return (
        <div className="flex flex-col gap-1.5">
            <div className="relative h-[72px]">
                {/* Trilho e as divisões de 6 em 6 horas: sem elas o olho não
                    consegue estimar a posição de um marcador solto. */}
                <div className="absolute inset-x-0 top-[34px] h-2 rounded-full bg-muted" />
                {[0, 25, 50, 75, 100].map((p) => (
                    <div
                        key={p}
                        className="absolute top-[30px] h-5 w-px bg-border"
                        style={{ left: `${p}%` }}
                        aria-hidden
                    />
                ))}

                {/* A faixa do maior intervalo, atrás dos marcadores. */}
                {maior && maior.gap > 0 && (
                    <div
                        className="absolute top-[34px] h-2 rounded-full bg-amber-200"
                        style={{
                            left: `${(maior.de / 1440) * 100}%`,
                            width: `${(maior.gap / 1440) * 100}%`,
                        }}
                        aria-hidden
                    />
                )}

                {marcas.map(({ g, min }, i) => {
                    const acima = i % 2 === 0;
                    return (
                        <div
                            key={g.id}
                            className="absolute flex w-0 flex-col items-center"
                            style={{ left: `${(min / 1440) * 100}%`, top: acima ? 0 : '44px' }}
                            title={`${horaExibida(g)} em Brasília (${hhmm(g.hora_local)} local) · ${g.paginas.length} página(s)${g.ativa ? '' : ' · desligado'}`}
                        >
                            {acima && (
                                <span className={`whitespace-nowrap text-[11px] tabular-nums ${g.ativa ? 'font-medium' : 'text-muted-foreground'}`}>
                                    {horaExibida(g)}
                                </span>
                            )}
                            <span
                                className={`w-[3px] rounded-full ${acima ? 'mt-1 h-[22px]' : 'mb-1 h-[22px]'} ${
                                    g.ativa ? 'bg-foreground' : 'bg-muted-foreground/30'
                                }`}
                            />
                            {!acima && (
                                <span className={`whitespace-nowrap text-[11px] tabular-nums ${g.ativa ? 'font-medium' : 'text-muted-foreground'}`}>
                                    {horaExibida(g)}
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="flex justify-between text-[11px] tabular-nums text-muted-foreground">
                <span>00h</span><span>06h</span><span>12h</span><span>18h</span><span>24h</span>
            </div>

            {maior && maior.gap > 0 && (
                <p className="text-[11px] text-muted-foreground">
                    <span className="mr-1 inline-block h-2 w-3 rounded-full bg-amber-200 align-middle" />
                    Maior intervalo sem disparo: <strong className="font-medium text-foreground">{duracao(maior.gap)}</strong>,
                    entre {comoHora(maior.de)} e {comoHora(maior.ate)}.
                </p>
            )}
        </div>
    );
}
